/**
 * Copyright (c) 2020 IoTize SAS <contact@iotize.com>
 *
 * The following code is based on https://github.com/cosmosio/nested-property
 * It has been translated to Typescript (based on Javascript implementation)
 * Function behavior has been modified from original version
 *
 * @license nested-property https://github.com/cosmosio/nested-property
 *
 * The MIT License (MIT)
 *
 * Copyright (c) 2014-2015 Olivier Scherrer <pode.fr@gmail.com>
 */
/**
 * Get the property of an object nested in one or more objects
 * given an object such as a.b.c.d = 5, getNestedProperty(a, "b.c.d") will return 5.
 *
 * @param object the object to get the property from
 * @param property the path to the property as a string
 * @returns the object or the the property value if found
 */
export function getNestedProperty(object, property) {
    if (object && typeof object === 'object') {
        if (typeof property === 'string' && property !== '') {
            const split = property.split('.');
            return split.reduce(function (obj, prop) {
                return obj && obj[prop];
            }, object);
        }
        else if (typeof property === 'number') {
            return object[property];
        }
        else {
            return undefined;
        }
    }
    else {
        return undefined;
    }
}
/**
 * Tell if a nested object has a given property (or array a given index)
 * given an object such as a.b.c.d = 5, hasNestedProperty(a, "b.c.d") will return true.
 * It also returns true if the property is in the prototype chain.
 *
 * The MIT License (MIT)
 * Copyright (c) 2014-2015 Olivier Scherrer <pode.fr@gmail.com>
 *
 * @param object the object to get the property from
 * @param property the path to the property as a string
 * @param options:
 *  - own: set to reject properties from the prototype
 * @returns true if has (property in object), false otherwise
 */
export function hasNestedProperty(object, property, options = {}) {
    if (object && typeof object === 'object') {
        if (typeof property === 'string' && property !== '') {
            const split = property.split('.');
            return split.reduce(function (obj, prop, idx, array) {
                if (idx === array.length - 1) {
                    if (options.own) {
                        return !!(obj && obj.hasOwnProperty(prop));
                    }
                    else {
                        return !!(obj !== null && typeof obj === 'object' && prop in obj);
                    }
                }
                return obj && obj[prop];
            }, object);
        }
        else if (typeof property === 'number') {
            return property in object;
        }
        else {
            return false;
        }
    }
    else {
        return false;
    }
}
/**
 * Set the property of an object nested in one or more objects
 * If the property doesn't exist, it gets created.
 *
 * The MIT License (MIT)
 * Copyright (c) 2014-2015 Olivier Scherrer <pode.fr@gmail.com>
 *
 * @param object
 * @param property
 * @param value the value to set
 * @returns object if no assignment was made or the value if the assignment was made
 */
export function setNestedProperty(object, property, value) {
    if (object && typeof object === 'object') {
        if (typeof property === 'string' && property !== '') {
            const split = property.split('.');
            return split.reduce(function (obj, prop, idx) {
                const nextPropIsNumber = Number.isInteger(Number(split[idx + 1]));
                obj[prop] = obj[prop] || (nextPropIsNumber ? [] : {});
                if (split.length === idx + 1) {
                    obj[prop] = value;
                }
                return obj[prop];
            }, object);
        }
        else if (typeof property === 'number') {
            object[property] = value;
            return object[property];
        }
        else {
            return object;
        }
    }
    else {
        return object;
    }
}
